/**
 * Contexto da avaliação no item-ação: a nota de maturidade que fez o item
 * existir, o que aquele nível significa e a evidência que o instrumento sugere.
 *
 * Desde 24/ago/2026 o Portal da Unidade TAMBÉM recebe isto — e ali ele é o FATO
 * do FACR. Antes a unidade abria o card e a primeira coisa era a PERGUNTA do
 * catálogo, o que fazia o plano parecer um questionário: faltava o "o que foi
 * encontrado". A nota + rubrica ocupam esse lugar melhor do que o texto livre do
 * avaliador, porque são padronizadas e existem sempre.
 *
 * Desde 10/set/2026 as telas internas usam o MESMO bloco de Fato da unidade: a
 * nota fica igual, e o que é só do avaliador (texto do achado, edição, evidência
 * sugerida) entra por `acao`/`children`/`mostrarEvidencia`.
 *
 * É informativo: não entra em nenhum DTO e não muda o que é salvo.
 */
import { CATALOGO } from "@caint/data";
import type { NivelMaturidade } from "@caint/engine";
import type { PlanoItem } from "@caint/shared";
import type { ReactNode } from "react";
import {
  type DescricaoNivel,
  descricaoNivel,
  escalaCompleta,
  nivelValido,
} from "../../lib/escalaMaturidade.ts";
import "../../portal/portal.css";

/** Evidência sugerida por item do catálogo (irmão do `PERGUNTA_POR_ITEM`). */
const EVIDENCIA_POR_ITEM = new Map(CATALOGO.map((i) => [i.id, i.evidencia]));

export interface ContextoAvaliacao {
  /** Nota que gerou o item; `null` em item manual ou gatilho inválido. */
  readonly nota: DescricaoNivel | null;
  /** Evidência sugerida do catálogo; `null` se o item saiu do catálogo. */
  readonly evidencia: string | null;
  /** A avaliação mudou depois e o item saiu da faixa 0–2 (`gatilhoResolvido`). */
  readonly notaHistorica: boolean;
}

type ItemComContexto = Pick<PlanoItem, "itemId" | "maturidadeGatilho" | "gatilhoResolvido">;

/**
 * O que a tela pode mostrar deste item — decisão fora do JSX para ser testável.
 * Sem `mostrar` devolve `null` de imediato (opt-in explícito de quem chama); e
 * devolve `null` também quando não há nada a dizer, para não render caixa vazia.
 */
export function contextoAvaliacaoDoItem(item: ItemComContexto, mostrar?: boolean): ContextoAvaliacao | null {
  if (!mostrar) {
    return null;
  }
  // `maturidadeGatilho` é texto solto vindo do banco: valida antes de chegar ao
  // motor, que lança em valor inválido.
  const nivel: NivelMaturidade | null = nivelValido(item.maturidadeGatilho);
  const evidencia = EVIDENCIA_POR_ITEM.get(item.itemId)?.trim() || null;
  if (!nivel && !evidencia) {
    return null;
  }
  return {
    nota: nivel ? descricaoNivel(nivel) : null,
    evidencia,
    notaHistorica: item.gatilhoResolvido,
  };
}

/** Bloco de leitura no card: nota à vista, evidência sugerida a um clique. */
export function BlocoContextoAvaliacao({
  ctx,
  comoFato,
  mostrarEvidencia = !comoFato,
  acao,
  children,
}: {
  ctx: ContextoAvaliacao | null;
  comoFato?: boolean;
  /** Evidência sugerida é orientação para quem AVALIA: por padrão, fora do Fato. */
  mostrarEvidencia?: boolean;
  /** Link no canto do rótulo (ex.: "Editar achado", só nas telas internas). */
  acao?: ReactNode;
  /** Conteúdo extra abaixo da nota (texto do achado, edição, aviso). */
  children?: ReactNode;
}) {
  // Na unidade o bloco É a nota. Sem nota (item manual) e sem nada a acrescentar
  // não há Fato a mostrar — e sem isto sobraria uma caixa rotulada e vazia.
  if (!ctx?.nota && !children && !acao && !(mostrarEvidencia && ctx?.evidencia)) {
    return null;
  }
  return (
    <div className="pl-achado pl-ctx">
      {/* Na unidade este bloco É o Fato do FACR; internamente é contexto de apoio. */}
      <div className="pl-achado-hd">
        <span className="pl-achado-label">
          {/* Sem número: o Fato é LEITURA. Numerar aqui colidia com o passo 1 do
              plano, e dois "1" no mesmo card confundem quem só quer preencher. */}
          {comoFato ? "FATO — o que a avaliação encontrou" : "Contexto da avaliação"}
        </span>
        {acao}
      </div>

      {ctx?.nota && (
        <div className="pl-ctx-linha">
          <span className="pl-ctx-nota" style={{ background: ctx.nota.cor }}>
            {ctx.nota.rotulo}
          </span>
          <span className="pl-ctx-txt">
            {/* "inicial" porque o gatilho é o `mi` — não é a nota de hoje. */}
            <b>Nota da avaliação inicial: {ctx.nota.situacao}</b>
            <br />
            {ctx.nota.rubrica}
          </span>
        </div>
      )}

      {ctx?.notaHistorica && (
        <p className="pl-ctx-hist">
          A avaliação foi atualizada depois e este item saiu da faixa 0–2. A nota acima é a que originou o
          item.
        </p>
      )}

      {children}

      {/* Evidência sugerida é orientação para quem AVALIA. Na unidade ela lia
          como "faça isto", competindo com a ação que a própria unidade deve
          propor — por isso fica só nas telas internas. */}
      {ctx?.evidencia && mostrarEvidencia && (
        <details className="pl-ctx-evid">
          <summary>Evidência sugerida</summary>
          <p>{ctx.evidencia}</p>
        </details>
      )}

      {!comoFato && <p className="pl-ctx-rodape">Informativo — não altera o plano.</p>}
    </div>
  );
}

/**
 * Legenda da escala, uma vez por página. Fechada por padrão: é referência
 * estável, e a rubrica do nível de cada item já aparece dentro do card.
 */
export function LegendaMaturidade() {
  return (
    <details className="pl-ajuda pl-legenda">
      <summary>O que significam as notas 0 a 4</summary>
      <ul>
        {escalaCompleta().map((d) => (
          <li key={d.nivel} className="pl-legenda-item">
            <span className="pl-legenda-num" style={{ background: d.cor }}>
              {d.rotulo}
            </span>
            <span>
              <b>{d.situacao}</b> — {d.rubrica}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}
